import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {EntitySummary} from 'constants/UserConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import SchemaNameInput from 'schemas/SchemaNameInput';
import SchemaOwnerDisplay from 'schemas/SchemaOwnerDisplay';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';

// exported for test only
const baseTestAttrs = {component: 'SchemaMetadataSection'};
export const DATA_TEST_ATTRS = {
  name: {...baseTestAttrs, element: 'schemaName'},
  owner: {...baseTestAttrs, element: 'ownerName'},
  warehouseTable: {...baseTestAttrs, element: 'warehouseTable'},
};

interface SchemaMetadataSectionProps {
  isEditableWarehouseTableName: boolean;
  hideWarehouseTableName?: boolean;
  name?: string;
  ownerId?: string;
  ownerChoices?: Array<Owner> | null;
  sqlIdentifier?: string | null;
  schema?: AssaySchema | RequestTaskSchemaGraphQL | null;
  onNameChange: (name: string) => void;
  onOwnerChange: (owner: EntitySummary) => void;
  onSqlIdentifierChange: (sqlIdentifier: string) => void;
}

function SchemaMetadataSection(props: SchemaMetadataSectionProps): JSX.Element {
  const isSchemaArchived = props.schema != null && isArchived(props.schema);
  return (
    <div className={css(schemaMetadataSectionStyles.schemaMetadataSection)}>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>
          <SchemaNameInput
            isWritable={!isSchemaArchived}
            name={props.name}
            onNameChange={props.onNameChange}
            schema={props.schema}
            dataTestAttrs={DATA_TEST_ATTRS.name}
          />
        </div>
      </div>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>
          <SchemaOwnerDisplay
            ownerId={props.ownerId}
            ownerChoices={props.ownerChoices}
            onOwnerChange={props.onOwnerChange}
            schema={props.schema}
            dataTestAttrs={DATA_TEST_ATTRS.owner}
          />
        </div>
      </div>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        {BenchlingConfig.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME && !props.hideWarehouseTableName && (
          <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>
            <SchemaWarehouseNameInput
              isWritable={props.isEditableWarehouseTableName && !isSchemaArchived}
              schema={props.schema}
              sqlIdentifier={props.sqlIdentifier ?? ''}
              onSqlIdentifierChange={props.onSqlIdentifierChange}
              dataTestAttrs={DATA_TEST_ATTRS.warehouseTable}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export const schemaMetadataSectionStyles = StyleSheet.create({
  ownerDropdown: {
    flex: 1,
  },
  schemaMetadataSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
    width: '100%',
  },
  schemaMetadataColumn: {
    flex: 0.3,
    minWidth: 0,
  },
  schemaMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
});

export default SchemaMetadataSection;
